import React, { useContext, useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import { DependencyOption } from '../../tasks/StartCondition';
import { errorPopulator } from '../../../utility/inputUtils';
import { ErroredModel } from '../../../types/ErroredModel';
import { EmailTemplateStartCondition } from '../../../types/EmailTemplate';
import { DisableContext } from '../../../contexts/DisableContextProvider';

export interface IEmailStartConditionProps<T> {
  emailTemplate: T;
  setEmailTemplate: (template: T) => void;
  dependencyOptions: DependencyOption[];
  disabledTriggers?: EmailTemplateStartCondition[];
}

// Mapped trigger options
const TRIGGER_OPTIONS: { label: string; value: EmailTemplateStartCondition }[] = [
  { label: 'Manual', value: EmailTemplateStartCondition.MANUAL },
  { label: 'Offset (Days)', value: EmailTemplateStartCondition.DATE_OFFSET },
  { label: 'On Event Completion', value: EmailTemplateStartCondition.EVENT_DEPENDENCY },
  { label: 'On Task Completion', value: EmailTemplateStartCondition.TASK_DEPENDENCY },
];

export default function EmailStartCondition<
  T extends {
    offset: number | null;
    depends_on_uuid?: string | null;
    start_condition: EmailTemplateStartCondition;
    uuid?: string | null;
  },
>({ emailTemplate, setEmailTemplate, dependencyOptions, disabledTriggers = [] }: IEmailStartConditionProps<T>) {
  const { disabled } = useContext(DisableContext);
  const [triggerType, setTriggerType] = useState<EmailTemplateStartCondition>(EmailTemplateStartCondition.MANUAL);

  // Initialize local trigger state when modal opens or data changes
  useEffect(() => {
    if (!emailTemplate) return;
    setTriggerType(emailTemplate.start_condition || EmailTemplateStartCondition.MANUAL);
  }, [emailTemplate.start_condition]);

  const handleTriggerTypeChange = (value: EmailTemplateStartCondition | undefined) => {
    if (!value) return;
    setTriggerType(value);

    // reset old dependencies and offsets
    const reset: Pick<T, 'offset' | 'depends_on_uuid'> = {
      offset: null,
      depends_on_uuid: null,
    };

    const updated = { ...emailTemplate, ...reset, start_condition: value };

    // configure according to selected type
    switch (value) {
      case EmailTemplateStartCondition.DATE_OFFSET:
        updated.offset = 0;
        break;
      case EmailTemplateStartCondition.TASK_DEPENDENCY:
        updated.depends_on_uuid = null;
        break;
      default:
        break;
    }

    setEmailTemplate(updated);
  };

  const availableOptions = _.reject(TRIGGER_OPTIONS, (opt) => _.includes(disabledTriggers, opt.value));

  // exclude self task from dependency options
  const dependencyTasks = _.reject(dependencyOptions, {
    uuid: emailTemplate.uuid || '',
  });

  const dateOffsetInput = (
    <Tooltip title="Send email after specified number of days from event start" placement="right">
      <TextField
        label="Offset Days"
        type="number"
        value={emailTemplate.offset ?? ''}
        onChange={(e) =>
          setEmailTemplate({
            ...emailTemplate,
            offset: e.target.value.trim() === '' ? null : Number(e.target.value),
          })
        }
        disabled={disabled}
        {...errorPopulator(emailTemplate as ErroredModel, 'offset')}
      />
    </Tooltip>
  );

  const eventDependencyInput = (
    <Tooltip title="This email will be sent when the event is completed" placement="right">
      <TextField label="Trigger" value="On Event Completion" disabled />
    </Tooltip>
  );

  const taskDependencyInput = (
    <Tooltip title="This email will be sent when the selected task is completed" placement="right">
      <Autocomplete<DependencyOption>
        fullWidth
        options={dependencyTasks}
        getOptionLabel={(opt) => opt.name}
        value={
          _.find(dependencyTasks, {
            uuid: emailTemplate.depends_on_uuid || '',
          }) || null
        }
        onChange={(_, opt) =>
          setEmailTemplate({
            ...emailTemplate,
            depends_on_uuid: opt?.uuid ?? null,
            offset: null,
            start_condition: EmailTemplateStartCondition.TASK_DEPENDENCY,
          })
        }
        renderInput={(params) => <TextField {...params} label="Depends On Task" />}
        disabled={disabled}
      />
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Trigger Type Selector */}
      <Autocomplete
        options={availableOptions}
        onChange={(_, option) => handleTriggerTypeChange(option?.value)}
        getOptionLabel={(option) => option.label}
        value={_.find(availableOptions, { value: triggerType }) || null}
        disabled={disabled}
        fullWidth
        renderInput={(params) => <TextField {...params} label="Send Trigger" />}
      />

      {triggerType === EmailTemplateStartCondition.DATE_OFFSET && dateOffsetInput}

      {triggerType === EmailTemplateStartCondition.EVENT_DEPENDENCY && eventDependencyInput}

      {triggerType === EmailTemplateStartCondition.TASK_DEPENDENCY && taskDependencyInput}
    </Box>
  );
}
